import { useEffect, useState } from 'react';

/**
 * SW update prompt — surfaces when a new precached build is waiting.
 *
 * Today the scanner ships new builds via service-worker precache, but the
 * waiting SW only activates once every tab is closed. In practice couriers
 * keep the PWA open for full shifts, so they run yesterday's code without
 * knowing it. This banner shows the moment a new SW reaches `installed`,
 * with a one-tap Refresh that posts `SKIP_WAITING` (the SW already listens
 * for this) then reloads.
 *
 * Brand-neutral inline styling on purpose — keeps the banner readable
 * across all three triple-brand themes without pulling design tokens.
 */
const UpdatePrompt = () => {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handler = (event: Event) => {
      const sw = (event as CustomEvent<ServiceWorker>).detail;
      if (sw) {
        setWaiting(sw);
        setDismissed(false);
      }
    };
    window.addEventListener('sw-update-available', handler as EventListener);
    return () => {
      window.removeEventListener('sw-update-available', handler as EventListener);
    };
  }, []);

  if (!waiting || dismissed) return null;

  const refresh = () => {
    waiting.postMessage({ type: 'SKIP_WAITING' });
    window.location.reload();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: '#1f2937',
        color: '#fff',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
        fontSize: 14,
        maxWidth: 480,
        margin: '0 auto',
      }}
    >
      <span style={{ flex: 1 }}>A new version is available.</span>
      <button
        type="button"
        onClick={refresh}
        style={{
          padding: '8px 14px',
          minHeight: 36,
          background: '#fff',
          color: '#1f2937',
          border: 'none',
          borderRadius: 6,
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          textTransform: 'none',
          boxShadow: 'none',
        }}
      >
        Refresh
      </button>
      <button
        type="button"
        aria-label="Dismiss update prompt"
        onClick={() => setDismissed(true)}
        style={{
          width: 44,
          height: 44,
          minWidth: 44,
          padding: 0,
          background: 'transparent',
          color: '#fff',
          border: 'none',
          fontSize: 22,
          lineHeight: 1,
          cursor: 'pointer',
          textTransform: 'none',
          boxShadow: 'none',
        }}
      >
        &times;
      </button>
    </div>
  );
};

export default UpdatePrompt;
